'use client';

import { OtnGameOtnTeam } from '@/data/models/ot_nationals/otn_game_otn_team';
import { toNum } from '@/lib/utils';

type TeamComparisonProps = {
	homeTeamGame?: OtnGameOtnTeam;
	awayTeamGame?: OtnGameOtnTeam;
};

type ComparisonRow = { label: string; value: (t: OtnGameOtnTeam) => string; active: (t: OtnGameOtnTeam) => boolean };

const ratio = (made?: number, att?: number) => `${toNum(made)}/${toNum(att)}`;

const ROWS: ComparisonRow[] = [
	{ label: 'Total Yards', value: (t) => String(toNum(t.total_yards)), active: (t) => toNum(t.total_yards) !== 0 },
	{ label: 'Passing', value: (t) => String(toNum(t.passing_yards)), active: (t) => toNum(t.total_yards) !== 0 },
	{ label: 'Rushing', value: (t) => String(toNum(t.rushing_yards)), active: (t) => toNum(t.total_yards) !== 0 },
	{ label: 'First Downs', value: (t) => String(toNum(t.first_downs)), active: (t) => toNum(t.first_downs) !== 0 },
	{
		label: '3rd Down',
		value: (t) => ratio(t.third_down_conversions, t.third_down_attempts),
		active: (t) => toNum(t.third_down_attempts) !== 0,
	},
	{
		label: '4th Down',
		value: (t) => ratio(t.fourth_down_conversions, t.fourth_down_attempts),
		active: (t) => toNum(t.fourth_down_attempts) !== 0,
	},
	{ label: 'Turnovers', value: (t) => String(toNum(t.turnovers)), active: (t) => toNum(t.turnovers) !== 0 },
	{
		label: 'Penalties',
		value: (t) => `${toNum(t.penalties)}-${toNum(t.penalty_yards)}`,
		active: (t) => toNum(t.penalties) !== 0,
	},
];

/**
 * Side-by-side team totals. These columns only exist for games entered with the in-house stat
 * tool, so the whole block renders nothing for older games (every value is 0 or absent).
 */
const TeamComparison = ({ homeTeamGame, awayTeamGame }: TeamComparisonProps) => {
	if (!homeTeamGame || !awayTeamGame) return null;
	const rows = ROWS.filter((row) => row.active(homeTeamGame) || row.active(awayTeamGame));
	// Turnovers / penalties alone aren't enough to show the block; yardage or first downs are.
	if (!rows.some((row) => row.label === 'Total Yards' || row.label === 'First Downs')) return null;

	const homeCode = homeTeamGame.otn_team?.team_code ?? 'HOME';
	const awayCode = awayTeamGame.otn_team?.team_code ?? 'AWAY';

	return (
		<section className="otn-section-border flex w-full flex-col gap-6 bg-black px-6 py-6" data-testid="team-comparison">
			<div className="border-b-[2px] border-b-[#333] pb-6">
				<h2 className="otn-heading">Team Stats</h2>
			</div>
			<table className="w-full table-fixed text-[13px] leading-[150%]">
				<thead>
					<tr className="bg-otnBlack h-10 font-extrabold uppercase">
						<th className="pl-4 text-left">{homeCode}</th>
						<th className="text-center" />
						<th className="pr-4 text-right">{awayCode}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-[#333]">
					{rows.map((row) => (
						<tr key={row.label}>
							<td className="py-2 pl-4 text-left font-[600]">{row.value(homeTeamGame)}</td>
							<td className="text-otnGray py-2 text-center text-[11px] uppercase">{row.label}</td>
							<td className="py-2 pr-4 text-right font-[600]">{row.value(awayTeamGame)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</section>
	);
};

export default TeamComparison;
